import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const HomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 20px 40px;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--primary-700);
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.18);
  padding: 6px 14px;
  border-radius: var(--radius-pill);
  margin-bottom: 22px;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--secondary-color);
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2);
  }
`;

const HomeTitle = styled.h1`
  font-size: clamp(2.6rem, 6vw, 3.75rem);
  line-height: 1.04;
  margin: 0 0 18px;
  font-weight: 800;
  letter-spacing: -0.03em;
  text-align: center;
  background: linear-gradient(135deg, var(--primary-700) 0%, var(--primary-color) 55%, var(--info-color) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const HomeSubtitle = styled.h2`
  font-size: clamp(1.1rem, 2.4vw, 1.35rem);
  font-weight: 400;
  line-height: 1.55;
  margin-bottom: 36px;
  color: var(--ink-600);
  text-align: center;
  max-width: 720px;
`;

const SectionHead = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.7rem, 3.5vw, 2.1rem);
  margin: 0 0 14px;
  color: var(--ink-900);
  font-weight: 750;
  letter-spacing: -0.02em;
  text-align: center;
  width: 100%;
`;

const SectionDescription = styled.p`
  color: var(--ink-500);
  line-height: 1.7;
  text-align: center;
  max-width: 720px;
  margin: 0 0 36px;
  font-size: 1.05rem;
`;

const FeaturesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
  gap: 22px;
  width: 100%;
  max-width: 1100px;
  margin-bottom: 8px;
`;

const FeatureCard = styled.div`
  position: relative;
  background-color: var(--surface-0);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 26px 24px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  transition: transform var(--dur-base) var(--ease-out),
    box-shadow var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-out);

  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 3px;
    background: var(--grad-brand-soft);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform var(--dur-slow) var(--ease-out);
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-lg);
    border-color: rgba(33, 150, 243, 0.3);
  }

  &:hover::before {
    transform: scaleX(1);
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.12rem;
  margin: 0 0 10px;
  color: var(--ink-900);
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const FeatureDescription = styled.p`
  color: var(--ink-500);
  line-height: 1.65;
  font-size: 0.95rem;
  margin: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Button = styled(Link)`
  padding: 14px 28px;
  border-radius: var(--radius-pill);
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: -0.01em;
  transition: background-color var(--dur-base) var(--ease-out),
    transform var(--dur-base) var(--ease-spring),
    box-shadow var(--dur-base) var(--ease-out);

  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

const StartButton = styled(Button)`
  background-color: var(--secondary-color);
  color: white;
  box-shadow: var(--shadow-accent-green);

  &:hover {
    background-color: var(--secondary-700);
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(76, 175, 80, 0.36);
  }
`;

const AboutButton = styled(Button)`
  background-color: var(--surface-0);
  color: var(--primary-700);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-xs);

  &:hover {
    background-color: var(--surface-50);
    border-color: rgba(33, 150, 243, 0.35);
    transform: translateY(-3px);
    box-shadow: var(--shadow-md);
  }
`;

const Divider = styled.hr`
  width: 100%;
  max-width: 1100px;
  margin: 56px 0;
  border: none;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--border) 20%, var(--border) 80%, transparent);
`;

const HomePage: React.FC = () => {
  return (
    <HomeContainer>
      <Eyebrow>Interactive Concurrency Lab</Eyebrow>
      <HomeTitle>ThreadViz</HomeTitle>
      <HomeSubtitle>
        An interactive concurrency visualization tool for understanding multithreading behavior and patterns
      </HomeSubtitle>
      
      <SectionDescription>
        ThreadViz helps you learn and understand complex concurrency concepts through interactive visualizations.
        Watch threads interact in real-time, observe resource allocation, and gain insights into synchronization
        mechanisms used in modern software systems.
      </SectionDescription>
      
      <ButtonContainer>
        <StartButton to="/simulation">Start Visualizing</StartButton>
        <AboutButton to="/about">Learn More</AboutButton>
      </ButtonContainer>
      
      <Divider />

      <SectionHead>
        <SectionTitle>Available Simulations</SectionTitle>
        <SectionDescription>
          Explore these classic concurrency problems and patterns through our interactive visualizations.
          Each simulation demonstrates different aspects of multithreaded programming.
        </SectionDescription>
      </SectionHead>
      
      <FeaturesContainer>
        <FeatureCard>
          <FeatureTitle>Producer-Consumer</FeatureTitle>
          <FeatureDescription>
            Visualize multiple producers adding items to a bounded buffer while consumers remove them. Understand synchronization mechanisms for thread coordination.
          </FeatureDescription>
        </FeatureCard>
        
        <FeatureCard>
          <FeatureTitle>Dining Philosophers</FeatureTitle>
          <FeatureDescription>
            Explore the classic concurrency problem where philosophers share limited resources, demonstrating deadlock scenarios and resource allocation strategies.
          </FeatureDescription>
        </FeatureCard>
        
        <FeatureCard>
          <FeatureTitle>Reader-Writer</FeatureTitle>
          <FeatureDescription>
            See how concurrent read access with exclusive write access to a shared resource works. Understand the pattern for efficient concurrent data access.
          </FeatureDescription>
        </FeatureCard>
        
        <FeatureCard>
          <FeatureTitle>Barrier Synchronization</FeatureTitle>
          <FeatureDescription>
            Observe how threads can coordinate to reach a synchronization point before collectively proceeding. Understand thread coordination in parallel algorithms.
          </FeatureDescription>
        </FeatureCard>
        
        <FeatureCard>
          <FeatureTitle>Sleeping Barber</FeatureTitle>
          <FeatureDescription>
            Explore this classic concurrency problem modeling a barber shop with limited waiting capacity. Visualize producer-consumer concepts with service dynamics.
          </FeatureDescription>
        </FeatureCard>
        
        <FeatureCard>
          <FeatureTitle>Cigarette Smokers</FeatureTitle>
          <FeatureDescription>
            Discover this interesting synchronization challenge where smokers with different resources must coordinate to create cigarettes. Analyze resource allocation problems.
          </FeatureDescription>
        </FeatureCard>
      </FeaturesContainer>
      
      <Divider />

      <SectionHead>
        <SectionTitle>Key Features</SectionTitle>
        <SectionDescription>
          ThreadViz offers powerful tools to help you understand concurrency concepts.
        </SectionDescription>
      </SectionHead>
      
      <FeaturesContainer>
        <FeatureCard>
          <FeatureTitle>Real-time Visualization</FeatureTitle>
          <FeatureDescription>
            Watch threads in action with color-coded states and dynamic positioning. See relationships between threads and shared resources visually.
          </FeatureDescription>
        </FeatureCard>
        
        <FeatureCard>
          <FeatureTitle>Event Logging</FeatureTitle>
          <FeatureDescription>
            Track thread state changes with a detailed event log. Understand the sequence of operations and transitions in concurrent systems.
          </FeatureDescription>
        </FeatureCard>
        
        <FeatureCard>
          <FeatureTitle>Configurable Parameters</FeatureTitle>
          <FeatureDescription>
            Adjust simulation parameters like thread count, execution delays, and buffer sizes to experiment with different scenarios.
          </FeatureDescription>
        </FeatureCard>
      </FeaturesContainer>

      <StartButton to="/simulation" style={{ marginTop: '40px' }}>Start Visualizing</StartButton>
    </HomeContainer>
  );
};

export default HomePage; 